/* Customer reviews: search, rating filter, topics, sort, pagination, helpful votes and the write-a-review dialog. */
if (!customElements.get('lp-reviews')) {
  const VOTES_KEY = 'lp-review-votes';

  const readVotes = () => {
    try {
      return JSON.parse(localStorage.getItem(VOTES_KEY) || '{}');
    } catch {
      return {};
    }
  };
  const writeVotes = (votes) => {
    try {
      localStorage.setItem(VOTES_KEY, JSON.stringify(votes));
    } catch {
      /* storage unavailable: votes still count for this visit */
    }
  };

  // Only a review's own words count for search and topics
  const reviewText = (item) =>
    `${item.querySelector('.lp-review__title')?.textContent || ''} ${item.querySelector('.lp-review__body')?.textContent || ''}`.toLowerCase();

  class LpReviews extends HTMLElement {
    connectedCallback() {
      // Sections can be re-attached (theme editor, DOM moves); only wire things up once
      if (this.initialized) return;
      this.initialized = true;
      this.items = [...this.querySelectorAll('.lp-review')];
      this.perPage = Number(this.dataset.perPage) || 5;
      this.page = 1;
      this.state = { query: '', stars: '', topic: '', sort: 'relevant' };
      this.productKey = location.pathname;

      this.search = this.querySelector('[data-review-search]');
      this.rating = this.querySelector('[data-review-rating]');
      this.sort = this.querySelector('[data-review-sort]');
      this.list = this.querySelector('[data-review-list]');
      this.pages = this.querySelector('[data-review-pages]');
      this.empty = this.querySelector('[data-review-empty]');
      this.showing = this.querySelector('[data-review-showing]');
      this.dialog = this.querySelector('[data-review-dialog]');

      this.search?.addEventListener('input', () => this.update({ query: this.search.value.trim().toLowerCase() }));
      this.rating?.addEventListener('change', () => this.update({ stars: this.rating.value }));
      this.sort?.addEventListener('change', () => this.update({ sort: this.sort.value }));
      this.querySelectorAll('[data-filter-stars]').forEach((button) =>
        button.addEventListener('click', () => {
          const stars = this.state.stars === button.dataset.filterStars ? '' : button.dataset.filterStars;
          if (this.rating) this.rating.value = stars;
          this.update({ stars });
        })
      );

      this.setupVotes();
      this.setupTopics();
      this.formatDates();
      this.setupDialog();
      this.render();
    }

    update(change) {
      Object.assign(this.state, change);
      this.page = 1;
      this.render();
    }

    matches(item) {
      const { query, stars, topic } = this.state;
      const text = reviewText(item);
      if (stars && item.dataset.stars !== stars) return false;
      if (query && !text.includes(query)) return false;
      if (topic && !text.includes(topic)) return false;
      return true;
    }

    sorted(items) {
      // Relevance favours well-rated reviews that other shoppers found helpful
      const score = (item) => Number(item.dataset.stars) * 2 + Number(item.dataset.up) - Number(item.dataset.down);
      const by = {
        relevant: (a, b) => score(b) - score(a) || b.dataset.date.localeCompare(a.dataset.date),
        recent: (a, b) => b.dataset.date.localeCompare(a.dataset.date),
        highest: (a, b) => b.dataset.stars - a.dataset.stars || b.dataset.date.localeCompare(a.dataset.date),
        lowest: (a, b) => a.dataset.stars - b.dataset.stars || b.dataset.date.localeCompare(a.dataset.date),
        helpful: (a, b) => Number(b.dataset.up) - Number(a.dataset.up),
      };
      return [...items].sort(by[this.state.sort] || by.relevant);
    }

    render() {
      const visible = this.sorted(this.items.filter((item) => this.matches(item)));
      const pageCount = Math.max(1, Math.ceil(visible.length / this.perPage));
      this.page = Math.min(this.page, pageCount);
      const start = (this.page - 1) * this.perPage;
      const shown = new Set(visible.slice(start, start + this.perPage));

      visible.forEach((item) => this.list.append(item));
      this.items.forEach((item) => (item.hidden = !shown.has(item)));
      if (this.empty) this.empty.hidden = visible.length > 0;
      if (this.showing) {
        this.showing.textContent = visible.length
          ? `${start + 1}–${Math.min(start + this.perPage, visible.length)} of ${visible.length} reviews`
          : '';
      }
      this.querySelectorAll('[data-filter-stars]').forEach((button) =>
        button.classList.toggle('is-active', button.dataset.filterStars === this.state.stars)
      );
      this.renderPages(pageCount);
    }

    renderPages(pageCount) {
      if (!this.pages) return;
      this.pages.replaceChildren();
      if (pageCount < 2) return;
      const add = (label, page, { current = false, aria } = {}) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        if (aria) button.setAttribute('aria-label', aria);
        if (current) button.setAttribute('aria-current', 'page');
        button.disabled = page < 1 || page > pageCount;
        button.addEventListener('click', () => {
          this.page = page;
          this.render();
          this.list.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        this.pages.append(button);
      };
      add('‹', this.page - 1, { aria: 'Previous page' });
      for (let i = 1; i <= pageCount; i++) add(String(i), i, { current: i === this.page, aria: `Page ${i}` });
      add('›', this.page + 1, { aria: 'Next page' });
    }

    setupTopics() {
      const wrap = this.querySelector('.lp-reviews__topics');
      const list = this.querySelector('[data-review-topics]');
      if (!wrap || !list) return;
      const topics = (this.dataset.topics || '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean)
        .map((topic) => ({ topic, count: this.items.filter((i) => reviewText(i).includes(topic)).length }))
        .filter((t) => t.count >= 2)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
      if (!topics.length) return;
      topics.forEach(({ topic }) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lp-reviews__topic';
        button.textContent = topic;
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => {
          const active = this.state.topic === topic ? '' : topic;
          list.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b.textContent === active)));
          this.update({ topic: active });
        });
        li.append(button);
        list.append(li);
      });
      wrap.hidden = false;
    }

    setupVotes() {
      const votes = readVotes();
      this.items.forEach((item) => {
        const key = `${this.productKey}#${item.dataset.index}`;
        item.querySelectorAll('[data-vote]').forEach((button) => {
          const type = button.dataset.vote;
          const count = button.querySelector('[data-vote-count]');
          if (votes[key] === type) {
            button.setAttribute('aria-pressed', 'true');
            count.textContent = Number(count.textContent) + 1;
          }
          button.addEventListener('click', () => {
            const current = readVotes();
            const previous = current[key];
            item.querySelectorAll('[data-vote]').forEach((other) => {
              if (other.getAttribute('aria-pressed') === 'true') {
                const c = other.querySelector('[data-vote-count]');
                c.textContent = Number(c.textContent) - 1;
                other.setAttribute('aria-pressed', 'false');
              }
            });
            if (previous === type) {
              delete current[key];
            } else {
              current[key] = type;
              button.setAttribute('aria-pressed', 'true');
              count.textContent = Number(count.textContent) + 1;
            }
            writeVotes(current);
          });
        });
      });
    }

    formatDates() {
      const locale = document.documentElement.lang || undefined;
      const format = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
      this.querySelectorAll('.lp-review__date').forEach((time) => {
        const date = new Date(`${time.getAttribute('datetime')}T12:00:00`);
        if (!Number.isNaN(date.getTime())) time.textContent = format.format(date);
      });
    }

    setupDialog() {
      if (!this.dialog) return;
      this.querySelectorAll('[data-review-open]').forEach((button) =>
        button.addEventListener('click', () => this.dialog.showModal())
      );
      this.dialog.querySelector('[data-review-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      // After a submit Shopify reloads the page; reopen the dialog to show the result
      if (this.dialog.querySelector('[data-review-posted], .lp-review-form__error')) this.dialog.showModal();
    }
  }

  customElements.define('lp-reviews', LpReviews);
}
